import { Link } from "react-router-dom";
import { ShieldCheck, FileLock, Database, Eye, UserCircleCheck, Lock, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import Header from "../components/Header";
import api from "../lib/api";

function Section({ icon: Icon, title, children, testid }) {
    return (
        <section className="bg-white border border-slate-200 rounded-md p-5" data-testid={testid}>
            <div className="flex items-center gap-2.5 mb-2">
                <div className="h-8 w-8 rounded-md bg-civic-50 border border-civic-100 flex items-center justify-center">
                    <Icon size={16} weight="duotone" className="text-civic-600" />
                </div>
                <h2 className="font-heading font-bold text-base text-slate-900">{title}</h2>
            </div>
            <div className="text-sm text-slate-600 leading-relaxed space-y-2">{children}</div>
        </section>
    );
}

export default function Privacy() {
    const [supportEmail, setSupportEmail] = useState("");
    useEffect(() => {
        api.get("/settings").then(({ data }) => setSupportEmail(data?.support_email || "")).catch(() => {});
    }, []);

    const lastUpdated = "February 2026";

    return (
        <div className="min-h-screen bg-slate-50 pb-16" data-testid="privacy-page">
            <Header />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6">
                <Link
                    to="/about"
                    className="inline-flex items-center gap-1.5 text-xs text-civic-700 hover:text-civic-900 hover:underline underline-offset-4"
                    data-testid="privacy-back-link"
                >
                    <ArrowLeft size={14} weight="bold" /> About E-Vote
                </Link>

                <div className="mt-16">
                    <div className="overline text-civic-600 text-[10px]">Legal</div>
                    <div className="mt-2 flex items-center gap-3">
                        <ShieldCheck size={28} weight="duotone" className="text-civic-600" />
                        <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tight text-slate-900 break-words leading-tight" data-testid="privacy-title">
                            Privacy <span className="text-civic-600">Policy</span>
                        </h1>
                    </div>
                    <p className="mt-2 text-xs text-slate-500 hash-mono" data-testid="privacy-updated">Last updated: {lastUpdated}</p>
                    <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-3xl">
                        E-VoteVerify.com is built around a single non-negotiable principle: <strong>your ballot is anonymous</strong>. This page
                        explains, in plain English, what data we collect, how it's stored, and the mathematical guarantees that keep
                        your vote unlinkable from your identity.
                    </p>
                </div>

                <div className="mt-8 space-y-4">
                    <Section icon={FileLock} title="The two-ledger guarantee" testid="privacy-two-ledgers">
                        <p>
                            We operate two independent records that <em>never</em> reference each other:
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-1">
                            <li>
                                <strong>Blockchain ledger</strong> — anonymous, hash-chained blocks containing only the election ID,
                                your choices, and a SHA-256 hash linking each block to the previous one. No voter ID. No name. No
                                IP address inside a block.
                            </li>
                            <li>
                                <strong>Voter roll (vote_log)</strong> — records that a voter participated in an election, so the
                                system can enforce "one vote per voter." This roll never contains your choice.
                            </li>
                        </ul>
                        <p>
                            The two ledgers reconcile only by totals: <span className="hash-mono text-civic-700">|vote_log| == |blockchain|</span>.
                            Beyond that, they share no foreign keys. There is no SQL join that can recover which voter cast which block.
                        </p>
                    </Section>

                    <Section icon={Database} title="What we collect" testid="privacy-collect">
                        <p>To register and authenticate voters, we store:</p>
                        <ul className="list-disc list-inside space-y-1 ml-1">
                            <li>Voter ID issued by your election authority</li>
                            <li>Name, mailing address, date of birth, precinct, and (optionally) email and mobile number — used only to verify eligibility and to deliver election notifications</li>
                            <li>A <strong>bcrypt hash</strong> of your PIN (the PIN itself is never stored and cannot be recovered from the hash)</li>
                        </ul>
                        <p>When you cast a vote we additionally record, on the public voter roll:</p>
                        <ul className="list-disc list-inside space-y-1 ml-1">
                            <li>Timestamp of participation</li>
                            <li>IP address and a VPN/datacenter flag — strictly for fraud detection, displayed publicly on the "Who voted" list so any irregularity is visible to all observers</li>
                            <li>If the election is marked <em>Secure</em>, a face photo captured at the moment of vote submission (also publicly auditable)</li>
                        </ul>
                        <p>
                            None of the above ever enters a blockchain block. The block contains only the choices.
                        </p>
                    </Section>

                    <Section icon={Eye} title="What is public" testid="privacy-public">
                        <p>
                            The blockchain Explorer, Results, and Audit pages are open to anyone, no login required. They expose:
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-1">
                            <li>Every block's contents (election + choices) and its SHA-256 hash</li>
                            <li>The voter roll for closed elections — name, address, precinct, IP/VPN flag, and (for Secure elections) face photo of each voter who participated</li>
                            <li>Per-election totals and tallies, sealed until the polls close</li>
                        </ul>
                        <p className="text-slate-500">
                            What is <strong>not</strong> public, and is never derivable from the public data: which specific block belongs to which voter.
                        </p>
                    </Section>

                    <Section icon={Lock} title="How we secure your data" testid="privacy-security">
                        <ul className="list-disc list-inside space-y-1 ml-1">
                            <li><strong>PINs</strong> are hashed with bcrypt — the same one-way function used by banks.</li>
                            <li><strong>Sessions</strong> are issued as short-lived JWT tokens, scoped to a single role (voter or admin).</li>
                            <li><strong>Blocks</strong> are tamper-evident: every block hashes the previous block's hash, so changing one entry invalidates every entry after it — instantly visible to anyone with a copy of the chain.</li>
                            <li><strong>VPN/datacenter detection</strong> on every cast vote, surfaced publicly so observers can flag suspicious participation.</li>
                        </ul>
                    </Section>

                    <Section icon={UserCircleCheck} title="Your rights" testid="privacy-rights">
                        <ul className="list-disc list-inside space-y-1 ml-1">
                            <li><strong>Access</strong> — your election authority can confirm which records exist for your voter ID.</li>
                            <li><strong>Correction</strong> — request fixes to your name, address, precinct, or contact details through your authority.</li>
                            <li><strong>Notification opt-out</strong> — request to be excluded from welcome / reminder / results email blasts.</li>
                            <li><strong>Erasure of identity</strong> — at the discretion of your election authority and any applicable retention law. Note that erasing a voter record from the roll does not (and cannot) remove their already-cast vote from the chain — the chain is anonymous by construction.</li>
                        </ul>
                    </Section>

                    <Section icon={EnvelopeSimple} title="Contact" testid="privacy-contact">
                        <p>
                            Questions about this policy, your record, or to exercise any of the rights above — please contact your
                            election authority{supportEmail ? <> at <a href={`mailto:${supportEmail}`} className="text-civic-700 underline-offset-4 hover:underline" data-testid="privacy-support-email">{supportEmail}</a></> : null}.
                        </p>
                    </Section>
                </div>

                <div className="mt-8 text-center text-xs text-slate-400 hash-mono">
                    E-VoteVerify.com · SHA-256 · two ledgers, one truth
                </div>
            </div>
        </div>
    );
}
